// src/app/components/sessions/EscalaDolor.tsx
// Indicador de dolor post-rutina (escala 1-10) que el paciente reporta
// al confirmar el cierre de su sesión. Componente recibido de Claude
// Design — copy literal salvo el path del comment de cabecera.
//
// Tokens hardcoded a propósito (deuda chica documentada en
// deuda-tecnica-conocida.md): la paleta verde→amarillo→rojo es escala
// de dolor específica, no parte del design system general del repo
// (theme.css). Re-evaluar en Sprint 2+ si se consolida.
//
// API: solo emite valor al padre via onConfirmar. NO persiste por sí
// solo. El padre (SesionCompletada) llama useRegistrarDolor en
// onConfirmar.

import { useState } from "react"

// ─── Types ────────────────────────────────────────────────────────────────

export interface EscalaDolorProps {
  /** Título arriba (default: "Rutina completada") */
  titulo?: string
  /** Subtítulo arriba (default: "Antes de cerrar, una pregunta corta.") */
  subtitulo?: string
  /** Pregunta principal */
  pregunta?: string
  /** Hint debajo de la pregunta */
  hint?: string
  /** Callback con el valor seleccionado 1..10 */
  onConfirmar: (valor: number) => void
  /** Si está enviando — disable del botón Confirmar durante el RPC call
   *  para prevenir doble-submit por click rápido. */
  submitting?: boolean
  /** Mensaje de error a mostrar si el RPC falló. Permite reintentar. */
  submitError?: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────

type Palette = { bg: string; stroke: string }

function paletteForLevel(level: number, max: number): Palette {
  const t = (level - 1) / (max - 1)
  const stops: Palette[] = [
    { bg: "#A8E0C6", stroke: "#0F7A4C" },
    { bg: "#D6EBA0", stroke: "#5C7A1E" },
    { bg: "#FFE08A", stroke: "#8B6900" },
    { bg: "#FFC07A", stroke: "#A0511E" },
    { bg: "#F8A2A2", stroke: "#A12626" },
  ]
  const scaled = t * (stops.length - 1)
  const idx = Math.min(stops.length - 2, Math.floor(scaled))
  const localT = scaled - idx
  return mixStop(stops[idx], stops[idx + 1], localT)
}
function hexToRgb(h: string): [number, number, number] {
  const m = h.replace("#", "")
  return [
    parseInt(m.slice(0, 2), 16),
    parseInt(m.slice(2, 4), 16),
    parseInt(m.slice(4, 6), 16),
  ]
}
function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.round(n).toString(16).padStart(2, "0")
  return `#${c(r)}${c(g)}${c(b)}`
}
function mixStop(a: Palette, b: Palette, t: number): Palette {
  const [ar, ag, ab] = hexToRgb(a.bg)
  const [br, bg, bb] = hexToRgb(b.bg)
  const [asr, asg, asb] = hexToRgb(a.stroke)
  const [bsr, bsg, bsb] = hexToRgb(b.stroke)
  return {
    bg: rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t),
    stroke: rgbToHex(
      asr + (bsr - asr) * t,
      asg + (bsg - asg) * t,
      asb + (bsb - asb) * t
    ),
  }
}

export function labelForLevel(n: number): string {
  if (n === 1) return "nada"
  if (n <= 3) return "leve"
  if (n <= 5) return "moderado"
  if (n <= 7) return "fuerte"
  return "intenso"
}

// ─── Face (paramétrico) ───────────────────────────────────────────────────

function Face({
  level,
  max,
  size = 44,
  stroke = "#111",
  fill = "none",
  strokeWidth = 1.8,
}: {
  level: number
  max: number
  size?: number
  stroke?: string
  fill?: string
  strokeWidth?: number
}) {
  const t = (level - 1) / (max - 1)
  const mouthY = 15
  const mouthCurve = 6 - t * 14
  const mouthOpen = t < 0.25
  const showTear = t > 0.85
  const browTilt = t > 0.65 ? (t - 0.65) * 12 : 0
  const eyeShape = t < 0.15 ? "happy" : t > 0.85 ? "sad" : "normal"

  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke={stroke}
        strokeWidth={strokeWidth}
        fill={fill}
      />
      {browTilt > 0 && (
        <>
          <line
            x1={7.2}
            y1={8 - browTilt * 0.3}
            x2={9.4}
            y2={8.6 + browTilt * 0.3}
            stroke={stroke}
            strokeWidth={strokeWidth * 0.9}
            strokeLinecap="round"
          />
          <line
            x1={14.6}
            y1={8.6 + browTilt * 0.3}
            x2={16.8}
            y2={8 - browTilt * 0.3}
            stroke={stroke}
            strokeWidth={strokeWidth * 0.9}
            strokeLinecap="round"
          />
        </>
      )}
      {eyeShape === "happy" ? (
        <>
          <path
            d="M 7 11 Q 8.5 9.5 10 11"
            stroke={stroke}
            strokeWidth={strokeWidth * 0.9}
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M 14 11 Q 15.5 9.5 17 11"
            stroke={stroke}
            strokeWidth={strokeWidth * 0.9}
            fill="none"
            strokeLinecap="round"
          />
        </>
      ) : eyeShape === "sad" ? (
        <>
          <path
            d="M 7.4 10.6 Q 8.5 11.4 9.6 10.6"
            stroke={stroke}
            strokeWidth={strokeWidth * 0.9}
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M 14.4 10.6 Q 15.5 11.4 16.6 10.6"
            stroke={stroke}
            strokeWidth={strokeWidth * 0.9}
            fill="none"
            strokeLinecap="round"
          />
        </>
      ) : (
        <>
          <circle cx="8.5" cy="10.5" r="0.9" fill={stroke} />
          <circle cx="15.5" cy="10.5" r="0.9" fill={stroke} />
        </>
      )}
      {mouthOpen ? (
        <path
          d={`M 8 ${mouthY - 0.5} Q 12 ${mouthY + mouthCurve - 0.5} 16 ${mouthY - 0.5} Q 12 ${mouthY + mouthCurve + 1.5} 8 ${mouthY - 0.5} Z`}
          fill={stroke}
        />
      ) : (
        <path
          d={`M 8 ${mouthY} Q 12 ${mouthY + mouthCurve} 16 ${mouthY}`}
          stroke={stroke}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
        />
      )}
      {showTear && (
        <path
          d="M 16.8 14 Q 17.6 16 16.8 17 Q 16 16 16.8 14 Z"
          fill={stroke}
        />
      )}
    </svg>
  )
}

// ─── Main component ──────────────────────────────────────────────────────

export function EscalaDolor({
  titulo = "Rutina completada",
  subtitulo = "Antes de cerrar, una pregunta corta.",
  pregunta = "¿Cuánto dolor sentiste durante el ejercicio?",
  hint = "Elegí la opción que mejor lo represente.",
  onConfirmar,
  submitting = false,
  submitError = null,
}: EscalaDolorProps) {
  const [value, setValue] = useState(0) // 0 = no seleccionado, 1..10
  const display = value || 1
  const palette = paletteForLevel(display, 10)
  const hasValue = value > 0

  const handleConfirm = () => {
    if (hasValue && !submitting) onConfirmar(value)
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="px-6 pt-3 pb-6 text-center border-b border-[#f1f3f5]">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#dcefd9] flex items-center justify-center">
          <svg
            viewBox="0 0 24 24"
            className="h-7 w-7"
            fill="none"
            stroke="#0a3c20"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="text-[20px] font-bold text-[#0e1413] mt-3">{titulo}</h1>
        <p className="text-[14px] text-[#5f6562] mt-1">{subtitulo}</p>
      </div>

      {/* Body */}
      <div className="px-6 pt-5 pb-6">
        <h2 className="text-[16px] font-bold text-[#0e1413] leading-snug">
          {pregunta}
        </h2>
        <p className="text-[13px] text-[#7d8480] mt-1 mb-5">{hint}</p>

        {/* Big face that morphs */}
        <div className="flex justify-center mb-2">
          <div
            className="h-28 w-28 rounded-full flex items-center justify-center transition-colors duration-200"
            style={{ background: hasValue ? palette.bg : "#f5f5ef" }}
          >
            <Face
              level={display}
              max={10}
              size={88}
              stroke={hasValue ? palette.stroke : "#c4c8c0"}
              strokeWidth={2}
            />
          </div>
        </div>

        {/* Big number + label */}
        <div className="text-center mb-5">
          <p
            className="text-[44px] font-extrabold tabular-nums leading-none"
            style={{ color: hasValue ? palette.stroke : "#c4c8c0" }}
          >
            {hasValue ? value : "—"}
            <span className="text-[20px] font-bold text-[#7d8480]">
              {hasValue ? "/10" : ""}
            </span>
          </p>
          <p
            className="text-[14px] font-semibold mt-1 capitalize"
            style={{ color: hasValue ? palette.stroke : "#c4c8c0" }}
          >
            {hasValue ? labelForLevel(value) : "Movés el slider"}
          </p>
        </div>

        {/* Slider */}
        <div className="relative px-1">
          <div
            className="h-3 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, #A8E0C6 0%, #D6EBA0 25%, #FFE08A 50%, #FFC07A 75%, #F8A2A2 100%)",
            }}
          />
          <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-3 w-px bg-white/50" />
            ))}
          </div>
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={value || 5}
            onChange={(e) => setValue(parseInt(e.target.value, 10))}
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-10 cursor-pointer appearance-none bg-transparent focus:outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-10 [&::-webkit-slider-thumb]:w-10 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-transparent [&::-moz-range-thumb]:h-10 [&::-moz-range-thumb]:w-10 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-transparent [&::-moz-range-thumb]:border-0"
          />
          {hasValue && (
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
              style={{ left: `${((value - 1) / 9) * 100}%` }}
            >
              <div
                className="h-8 w-8 rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.2)] flex items-center justify-center border-2"
                style={{ borderColor: palette.stroke }}
              >
                <span
                  className="text-[12px] font-extrabold tabular-nums"
                  style={{ color: palette.stroke }}
                >
                  {value}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Anchors */}
        <div className="flex justify-between mt-3">
          <div className="text-center">
            <p className="text-[11px] font-bold text-[#0e1413]">1</p>
            <p className="text-[10px] text-[#7d8480]">nada</p>
          </div>
          <div className="text-center">
            <p className="text-[11px] font-bold text-[#0e1413]">5</p>
            <p className="text-[10px] text-[#7d8480]">moderado</p>
          </div>
          <div className="text-center">
            <p className="text-[11px] font-bold text-[#0e1413]">10</p>
            <p className="text-[10px] text-[#7d8480]">intenso</p>
          </div>
        </div>

        {/* Quick tap numbers */}
        <div className="mt-5 grid grid-cols-5 gap-1.5">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
            const isSelected = value === n
            const p = paletteForLevel(n, 10)
            return (
              <button
                key={n}
                onClick={() => setValue(n)}
                className={`h-9 rounded-lg text-[13px] font-bold tabular-nums transition-all ${
                  isSelected
                    ? "shadow-sm scale-105"
                    : "bg-[#f5f5ef] text-[#5f6562] hover:bg-[#eaeae3]"
                }`}
                style={
                  isSelected ? { background: p.bg, color: p.stroke } : {}
                }
              >
                {n}
              </button>
            )
          })}
        </div>

        {!hasValue && (
          <p className="text-center text-[11px] text-[#7d8480] mt-2">
            o tocá un número
          </p>
        )}

        {/* Error UX — permite reintentar tras fallo del RPC */}
        {submitError && (
          <p className="mt-4 text-center text-[12px] text-[#A12626] font-medium">
            {submitError}
          </p>
        )}

        {/* Confirm */}
        <button
          disabled={!hasValue || submitting}
          onClick={handleConfirm}
          className={`mt-5 w-full py-3.5 rounded-xl text-[15px] font-bold transition-colors ${
            hasValue && !submitting
              ? "bg-[#0e1413] text-white hover:bg-[#1f2624]"
              : "bg-[#f5f5ef] text-[#c4c8c0] cursor-not-allowed"
          }`}
        >
          {submitting ? "Guardando…" : "Confirmar"}
        </button>
      </div>
    </div>
  )
}

export default EscalaDolor
